import type {
  ClimbDensityCollection,
  DatasetOption,
  ForecastQuery,
  ForecastModelsResponse,
  ForecastResponse,
  LaunchAreaCollection,
  SiteOption,
  ThermalEpisodeCollection,
  WingOption
} from "./types";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

export const REFERENCE_SINK_RATE_MS = 1.16;

export type ApiErrorKind =
  | "aborted"
  | "http_status"
  | "invalid_payload"
  | "network";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly code: string | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null = null, code: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.code = code;
  }
}

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function getJson<T>(
  path: string,
  signal?: AbortSignal,
  cache: RequestCache = "default"
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      cache,
      headers: { Accept: "application/json" },
      signal
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("aborted", "Request cancelled");
    }
    throw new ApiError(
      "network",
      error instanceof Error ? error.message : "Network request failed"
    );
  }
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    let code: string | null = null;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") {
        detail = body.detail;
      } else if (isRecord(body.detail)) {
        if (typeof body.detail.message === "string") detail = body.detail.message;
        if (typeof body.detail.code === "string") code = body.detail.code;
      }
    } catch {
      // A proxy or upstream can return HTML. The status is still useful.
    }
    throw new ApiError("http_status", detail, response.status, code);
  }
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      "invalid_payload",
      "The server returned an unreadable response",
      response.status
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isSiteThermalMap(value: unknown): boolean {
  if (value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  const center = value.center;
  return (
    typeof value.dataset === "string" &&
    value.dataset.length > 0 &&
    Array.isArray(center) &&
    center.length === 2 &&
    isFiniteNumber(center[0]) &&
    isFiniteNumber(center[1]) &&
    isFiniteNumber(value.zoom)
  );
}

function isSiteOption(value: unknown): value is SiteOption {
  if (!isRecord(value)) {
    return false;
  }
  const requiredStrings = [
    value.slug,
    value.name,
    value.timezone,
    value.country_code,
    value.country_name,
    value.region_code,
    value.region_name,
    value.locality,
    value.full_location,
    value.canonical_site_slug,
    value.canonical_path
  ];
  return (
    requiredStrings.every((field) => typeof field === "string" && field.trim().length > 0) &&
    isFiniteNumber(value.latitude) &&
    isFiniteNumber(value.longitude) &&
    (value.elevation_ft === null || isFiniteNumber(value.elevation_ft)) &&
    typeof value.ridge_site === "boolean" &&
    typeof value.canonical_path === "string" &&
    value.canonical_path.startsWith("/forecast/") &&
    isSiteThermalMap(value.thermal_map)
  );
}

function parseSiteCatalog(value: unknown): SiteOption[] {
  if (!Array.isArray(value) || value.length === 0 || !value.every(isSiteOption)) {
    throw new ApiError(
      "invalid_payload",
      "The launch catalog is missing required canonical site fields"
    );
  }
  return value;
}

function forecastParams(query: ForecastQuery): URLSearchParams {
  const params = new URLSearchParams({
    day_offset: String(query.dayOffset),
    mode: query.mode,
    sink_rate_ms: String(REFERENCE_SINK_RATE_MS),
    units: query.units ?? "us"
  });
  if (query.mode === "ppg") {
    if (query.cruiseAirspeedKt !== undefined) {
      params.set("cruise_airspeed_kt", String(query.cruiseAirspeedKt));
    }
    if (query.courseDegrees !== undefined) {
      params.set("course_degrees", String(query.courseDegrees));
    }
    if (query.cruiseAltitudeModelAglFt !== undefined) {
      params.set(
        "cruise_altitude_model_agl_ft",
        String(query.cruiseAltitudeModelAglFt)
      );
    }
    if (query.launchHeadingDegrees !== undefined) {
      params.set("launch_heading_degrees", String(query.launchHeadingDegrees));
    }
  }
  params.set("model", query.model ?? "auto");
  return params;
}

export const api = {
  sites: async (signal?: AbortSignal) =>
    parseSiteCatalog(await getJson<unknown>("/forecast/sites", signal, "no-store")),
  // Kept until the windgram page removes its old selector. Public forecast
  // serializers never send a named wing.
  wings: (signal?: AbortSignal) => getJson<WingOption[]>("/forecast/wings", signal),
  forecast: (query: ForecastQuery, signal?: AbortSignal) => {
    const params = forecastParams(query);
    return getJson<ForecastResponse>(
      `/forecast/${encodeURIComponent(query.site)}?${params}`,
      signal
    );
  },
  models: (site: string, dayOffset: number, signal?: AbortSignal) => {
    const params = new URLSearchParams({ site, day_offset: String(dayOffset) });
    return getJson<ForecastModelsResponse>(`/forecast/models?${params}`, signal, "no-store");
  },
  datasets: (signal?: AbortSignal) => getJson<DatasetOption[]>("/datasets", signal),
  climbDensity: (dataset: string, signal?: AbortSignal, revision?: string) =>
    getJson<ClimbDensityCollection>(
      `/datasets/${encodeURIComponent(dataset)}/heatmap${revision ? `?revision=${encodeURIComponent(revision)}` : ""}`,
      signal
    ),
  thermalEpisodes: (dataset: string, signal?: AbortSignal, revision?: string) =>
    getJson<ThermalEpisodeCollection>(
      `/datasets/${encodeURIComponent(dataset)}/thermals${revision ? `?revision=${encodeURIComponent(revision)}` : ""}`,
      signal
    ),
  launchAreas: (dataset: string, signal?: AbortSignal, revision?: string) =>
    getJson<LaunchAreaCollection>(
      `/datasets/${encodeURIComponent(dataset)}/launches${revision ? `?revision=${encodeURIComponent(revision)}` : ""}`,
      signal
    )
};

export function staticWindgramUrl(query: ForecastQuery, locale?: string): string {
  const params = forecastParams(query);
  params.set("sensitivity", "true");
  if (locale) params.set("locale", locale);
  return apiUrl(`/forecast/${encodeURIComponent(query.site)}/windgram.svg?${params}`);
}
